import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { beautifyCSS } from "123tcs-common/utils/beautify";
import { createCSS } from "123tcs-common/utils/css";
import {
  formatCSStoSelectors,
  formatHTMLtoSelectors,
  formatSelectorsCSS,
} from "123tcs-common/utils/formatters";
import PropTypes from "prop-types";
import React from "react";
import AceEditor from "react-ace";
import { renderToStaticMarkup } from "react-dom/server";
import { SIMULATION_CARD } from "../../constants/simulation";
import {
  EditorSettingsProvider,
  useEditorSettings,
} from "../../contexts/EditorSettings";
import { LanguageProvider } from "../../contexts/Language";
import { SimulationProvider } from "../../contexts/Simulation";
import BalanceChecker from "../balance-checker/BalanceChecker";
import Card from "../balance-checker/Card";
import Transactions from "../balance-checker/Transactions";
import ExamplesButton from "./ExamplesButton";

import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/mode-css";

function BalanceCheckerCSSEditor(props) {
  const { value, onChange } = props;

  // Contexts
  const { settings } = useEditorSettings();

  // Constants
  const resultHTML = renderToStaticMarkup(
    <LanguageProvider>
      <EditorSettingsProvider>
        <SimulationProvider>
          <Card card={SIMULATION_CARD} />
          <Transactions card={SIMULATION_CARD} showEndOfLife />
        </SimulationProvider>
      </EditorSettingsProvider>
    </LanguageProvider>,
  );
  const formHTML = renderToStaticMarkup(
    <LanguageProvider>
      <EditorSettingsProvider xSettings={settings}>
        <SimulationProvider>
          <BalanceChecker />
        </SimulationProvider>
      </EditorSettingsProvider>
    </LanguageProvider>,
  );
  const CLASSES = [
    ...formatHTMLtoSelectors(resultHTML).classes,
    ...formatHTMLtoSelectors(formHTML).classes,
  ];
  const IDS = [
    ...formatHTMLtoSelectors(resultHTML).ids,
    ...formatHTMLtoSelectors(formHTML).ids,
  ];

  // Functions
  const handleDownload = () => {
    createCSS(value, "123tcs-balance-checker-stylesheet");
  };

  const handleBeautify = () => {
    onChange(beautifyCSS(value));
  };

  const handleSelectors = () => {
    const CLASSES_AND_IDS = formatCSStoSelectors(value);
    const CS = CLASSES_AND_IDS[0];
    const IS = CLASSES_AND_IDS[1];
    const MISSING_CLASSES = CLASSES.filter(
      (classItem) => !CS.includes(classItem),
    );
    const MISSING_IDS = IDS.filter((idItem) => !IS.includes(idItem));
    const MISSING_CLASSES_AND_IDS_CSS = formatSelectorsCSS(
      MISSING_CLASSES,
      MISSING_IDS,
    );
    const NEW_CSS = `${value}${MISSING_CLASSES_AND_IDS_CSS}`;

    onChange(NEW_CSS);
  };

  // Render
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        // display: "flex",
        // alignItems: "center",
        // justifyContent: "center",
      }}
    >
      <Box
        display="flex"
        sx={{
          padding: 1,
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          CSS
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Insert All Usable Selectors">
            <Button variant="contained" size="small" onClick={handleSelectors}>
              Fill Selectors
            </Button>
          </Tooltip>

          <ExamplesButton onLoad={onChange} />

          <Tooltip title="Beautify CSS">
            <IconButton onClick={handleBeautify}>
              <AutoFixHighIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Create an email to send CSS code to 123TCS support team">
            <IconButton
              href="mailto:support@123tcs.com?subject=Balance Checker Stylesheet"
              onClick={handleDownload}
            >
              <AttachEmailIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download CSS">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear">
            <IconButton color="error" onClick={() => onChange("")}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <AceEditor
        mode="css"
        // height="100%"
        height={
          settings.showHTML
            ? // (v. height - header - settings - padding) / 2 - CSS header - margin
              "calc((100vh - 66px - 77px - 32px) / 2 - 56px - 8px)"
            : // (v. height - header - settings - padding) / 2 - 2 * CSS header - margin
              "calc(100vh - 66px - 77px - 32px - 56px * 2 - 8px)"
        }
        width="100%"
        value={value}
        onChange={onChange}
        enableLiveAutocompletion
        enableBasicAutocompletion
        wrapEnabled
        showPrintMargin={false}
        setOptions={{
          enableKeyboardAccessibility: true,
          useWorker: false,
        }}
        sx={{ overflow: "hidden" }}
      />
    </Paper>
  );
}

BalanceCheckerCSSEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

BalanceCheckerCSSEditor.defaultProps = {
  value: "",
};

export default BalanceCheckerCSSEditor;
